import { ChargeCardPayment } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useFocusEffect } from 'expo-router/react-navigation';
import { TFunction } from 'i18next';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { transferStatusTag } from 'shared/components/TransferHistory/transfer.helper';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import TransactionListItem from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { monthYearFormatDateUtil } from 'shared/utils/string/date';
import { toTitleCase } from 'shared/utils/string/string.helpers';

import usePaymentHistory from './hook/usePaymentHistory';

const PaymentHistory = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();
  const { t: translatedText } = useTranslation(['believe', 'calendar']);
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const {
    error,
    loading,
    refetch,
    scheduledTransactions,
    processedTransactions,
    scheduledTransactionsLength,
    processedTransactionsLength,
    transactionLength,
  } = usePaymentHistory();

  const scheduledSecondaryText = (item: ChargeCardPayment) => {
    return `${monthYearFormatDateUtil(item?.date, ((key) => translatedText(`calendar:${key}`)) as unknown as TFunction)} • ${translatedText('believe:paymentHistory.to')}: ${translatedText('believe:varoBankAccount')} • `;
  };
  useViewDidFocusScreenAnalyticsEvent({
    key: 'paymentHistory',
  });
  const hasFocusedBefore = useRef(false);

  useFocusEffect(
    useCallback(() => {
      if (hasFocusedBefore.current) {
        refetch();
      } else {
        hasFocusedBefore.current = true;
      }
    }, [refetch]),
  );

  const renderGroupedTransactions = (
    transactionsByMonth: Record<string, ChargeCardPayment[]>,
    label: string,
  ) => {
    const handleOnCardClick = (id: string) => {
      router.navigate({
        pathname: '/believe/payment-history-details',
        params: {
          id,
        },
      });
    };

    return Object.entries(transactionsByMonth)?.map(([month, transactions]) => (
      <View key={month}>
        {label !== 'scheduled' ? <SectionDivider labelText={month} /> : null}
        {transactions.map((item) => (
          <View key={item?.id} style={styles.transactionListItemStyle}>
            <TransactionListItem
              amountStyle={label === 'scheduled' ? 'NEUTRAL' : 'POSITIVE'}
              isPending={false}
              titleText={translatedText('believe:paymentHistory.title')}
              amountText={item?.amount?.formattedWithPlusPrefix}
              accessibilityLabel=""
              secondaryText={
                label === 'scheduled'
                  ? scheduledSecondaryText(item)
                  : (null as unknown as undefined)
              }
              tags={
                label !== 'scheduled' &&
                item?.status &&
                transferStatusTag(item.status)
                  ? [
                      {
                        text: toTitleCase(item.status),
                        type: transferStatusTag(item.status),
                      },
                    ]
                  : (null as unknown as undefined)
              }
              onPress={() => handleOnCardClick(item.id)}
            />
          </View>
        ))}
      </View>
    ));
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('believe:paymentHistory.screenHeader'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={defaultMessage}
              illustrationKey="PLACEHOLDER_A"
              tryAgainAction={() => {}}
            />
          ) : null
        }
      />
      {transactionLength ? (
        <ScrollView style={styles.scrollView}>
          {scheduledTransactions && scheduledTransactionsLength ? (
            <>
              <SectionDivider
                labelText={translatedText('believe:paymentHistory.scheduled')}
              />
              {renderGroupedTransactions(scheduledTransactions, 'scheduled')}
            </>
          ) : null}

          {processedTransactions && processedTransactionsLength ? (
            <>{renderGroupedTransactions(processedTransactions, 'processed')}</>
          ) : null}
        </ScrollView>
      ) : (
        <View style={styles.emptyScreen}>
          <EmptyStateScreen
            headlineText={translatedText('believe:paymentHistory.emptyState')}
            illustrationKey="COINS_C"
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { backgroundColor: theme.color.background.primary, flex: 1 },
  emptyScreen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollView: {
    backgroundColor: theme.color.background.secondary,
  },
  transactionListItemStyle: {
    marginBottom: theme.spacing.s,
  },
});

export default PaymentHistory;
